import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { hasAdminRights } from '../utils/roleUtils';
import ProfilePicture from './shared/ProfilePicture';
import AddGuestModal from './AddGuestModal';
import ParticipantsModal from './ParticipantsModal';
import DeleteEventModal from './DeleteEventModal';
import {
  XMarkIcon,
  XCircleIcon,
  TrashIcon,
  UserGroupIcon,
  PencilIcon,
  ClockIcon,
  UserPlusIcon,
  CheckIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  CalendarIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import ConfirmationModal from './ConfirmationModal';
import AttributeLabel from './AttributeLabel';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { ModalShell, ModalActions, ModalSection } from './modals/core';
import type { ModalCloseReason } from './modals/core';

interface EventModalProps {
  event: any;
  events: any[];
  onClose: () => void;
  onDelete: (response: any) => void;
  onBook: (id: number, count: number) => void;
  refreshCalendar: () => void;
  onEventSelect: (event: any) => void;
  onEdit?: (event: any) => void;
}

interface Issues {
  insurance: boolean;
  vaccination: boolean;
  paid: boolean;
}

const EventModal: React.FC<EventModalProps> = ({
  event: initialEvent,
  events = [],
  onClose,
  onDelete,
  onBook,
  refreshCalendar,
  onEventSelect,
  onEdit
}) => {
  const { user } = useContext(AuthContext);
  const [event, setEvent] = useState(initialEvent);
  const [isBooked, setIsBooked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [, setIssues] = useState<Issues>({ insurance: true, vaccination: true, paid: true });
  const [myIssues, setMyIssues] = useState<string[]>([]);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {}
  });
  const [showEventList, setShowEventList] = useState(!event && events.length > 1);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    setShowEventList(!event && events.length > 1);
  }, [event, events.length]);

  // Optionen (Teilnehmer, Gast hinzufügen, Bearbeiten, Löschen) nur für Trainer & Vorstand.
  // hasAdminRights = Rolle BOARD oder TRAINER.
  const canManageEvent = hasAdminRights(user);
  const isPastEvent = event?.start && new Date(event.start) < new Date();

  // Booking status
  const bookingCount = event?.extendedProps?.bookingCount || 0;
  const maxBookable = event?.extendedProps?.bookable || 0;
  const isFullyBooked = bookingCount >= maxBookable && maxBookable > 0;
  const freeSlots = Math.max(0, maxBookable - bookingCount);

  // Determine modal variant/accent color
  const getAccentColor = () => {
    if (isBooked) return '#059669'; // success-600 - bereits gebucht
    if (isFullyBooked) return '#dc2626'; // red-600 - ausgebucht
    return '#16a34a'; // primary-600 - verfügbar
  };

  const getModalTitle = () => {
    if (isBooked) return 'Deine Buchung';
    if (isFullyBooked) return 'Termindetails';
    return 'Termin buchen';
  };

  const getModalSubtitle = () => {
    if (isBooked) return 'Du bist angemeldet';
    if (isFullyBooked) return 'Leider ausgebucht';
    return 'Buchung bestätigen';
  };

  const checkBookingStatus = useCallback(async () => {
    if (!event?.id) return;

    try {
      const response = await apiClient.get(`/api/members/check-event-booking/${event.id}`);
      if (response.data.success) {
        setIsBooked(response.data.isBooked);
        setIssues(response.data.issues || { insurance: true, vaccination: true, paid: true });
        const issuesArray = response.data.myIssues || [];
        const stringIssues = issuesArray
          .map((issue: any) => {
            if (typeof issue === 'string') return issue;
            if (issue && typeof issue.message === 'string') return issue.message;
            if (issue && typeof issue.toString === 'function') return issue.toString();
            return '';
          })
          .filter((issue: string) => issue.length > 0);
        setMyIssues(stringIssues);
      }
    } catch (error) {
      console.error('Error checking booking status:', error);
    }
  }, [event?.id]);

  const fetchParticipants = useCallback(async () => {
    if (!canManageEvent || !event?.id) return;

    try {
      const response = await apiClient.get(`/api/calendar/${event.id}/participants`);
      if (response.data.success) {
        setParticipants(response.data.participants || []);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  }, [canManageEvent, event?.id]);

  useEffect(() => {
    if (event?.id) {
      checkBookingStatus();
      fetchParticipants();
    }
  }, [event, checkBookingStatus, fetchParticipants]);

  const handleMainBook = async () => {
    if (isBooked) {
      setConfirmModal({
        isOpen: true,
        type: 'warning',
        title: 'Buchung stornieren?',
        message: 'Möchten Sie Ihre Buchung für diesen Termin **wirklich stornieren**?',
        variant: 'two',
        onConfirm: performCancel
      });
    } else {
      if (myIssues && myIssues.length > 0) {
        const issuesText = myIssues.map(issue => `• ${issue}`).join('\n');
        setConfirmModal({
          isOpen: true,
          type: 'warning',
          title: 'Wichtige Hinweise',
          message: `Sie haben folgende offene Punkte:\n${issuesText}\n\nMöchten Sie trotzdem buchen?`,
          variant: 'two',
          onConfirm: performBooking
        });
        return;
      }

      if (isPastEvent) {
        setConfirmModal({
          isOpen: true,
          type: 'question',
          title: 'Vergangenen Termin buchen?',
          message: 'Dieser Termin liegt in der Vergangenheit. Möchten Sie ihn trotzdem buchen?',
          variant: 'two',
          onConfirm: performBooking
        });
        return;
      }

      await performBooking();
    }
  };

  const performBooking = async () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
    setLoading(true);

    try {
      const response = await apiClient.post('/api/members/book-event', {
        kalender_id: event.id
      });

      if (response.data.success) {
        setIsBooked(true);
        onBook(event.id, (event.extendedProps?.bookingCount || 0) + 1);
        checkBookingStatus();
        refreshCalendar();

        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich gebucht',
          message: 'Ihr Platz wurde **erfolgreich reserviert**.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
      }
    } catch (error: any) {
      console.error('Error booking event:', error);
      let errorMessage = error.response?.data?.message || 'Die Buchung konnte nicht durchgeführt werden.';
      if (errorMessage.includes('Buchung fehlgeschlagen:')) {
        errorMessage = errorMessage.replace('Buchung fehlgeschlagen: ', '');
      }

      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Buchung fehlgeschlagen',
        message: errorMessage,
        variant: 'single',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
      });
    } finally {
      setLoading(false);
    }
  };

  const performCancel = async () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
    setLoading(true);

    try {
      const response = await apiClient.delete(`/api/members/cancel-event/${event.id}`);

      if (response.data.success) {
        setIsBooked(false);
        onBook(event.id, Math.max(0, (event.extendedProps?.bookingCount || 0) - 1));
        checkBookingStatus();
        refreshCalendar();

        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Buchung storniert',
          message: 'Ihre Buchung wurde erfolgreich storniert.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
      }
    } catch (error) {
      console.error('Error canceling event:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Stornierung fehlgeschlagen',
        message: 'Die Stornierung konnte nicht durchgeführt werden.',
        variant: 'single',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = (response: any) => {
    onDelete(response);
    setShowDeleteModal(false);
    onClose();
  };

  const handleEventSelect = (selectedEvent: any) => {
    setEvent(selectedEvent);
    setShowEventList(false);
    onEventSelect(selectedEvent);
  };

  const handleModalClose = (_reason: ModalCloseReason) => {
    onClose();
  };

  if (!event && events.length === 0) return null;

  // Event List View (Multiple events on same day)
  if (showEventList && events.length > 1) {
    return (
      <ModalShell
        open
        onClose={handleModalClose}
        variant="choice"
        size="md"
        icon={CalendarIcon}
        accent="brand"
        title="Termine"
        subtitle={format(events[0]?.start || new Date(), 'EEEE, dd. MMMM yyyy', { locale: de })}
      >
        <div className="space-y-3">
          {events.map((evt) => (
            <div
              key={evt.id}
              className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-500 cursor-pointer transition-all"
              onClick={() => handleEventSelect(evt)}
            >
              <div className="flex items-center gap-3">
                <div className="w-1 h-12 rounded-full" style={{ backgroundColor: evt.extendedProps?.color || '#16a34a' }}></div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white">{evt.title}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {format(evt.start, 'HH:mm')} - {format(evt.end, 'HH:mm')} Uhr
                  </p>
                </div>
                <div className="text-xs px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                  {evt.extendedProps?.bookingCount || 0}/{evt.extendedProps?.bookable || 0}
                </div>
              </div>
            </div>
          ))}
        </div>
      </ModalShell>
    );
  }

  if (!event) return null;

  // Calculate duration
  const durationMs = new Date(event.end).getTime() - new Date(event.start).getTime();
  const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
  const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
  const durationText = durationHours > 0
    ? `${durationHours}${durationMinutes > 0 ? `,${durationMinutes}` : ''} Stunde${durationHours > 1 ? 'n' : ''} Dauer`
    : `${durationMinutes} Minuten Dauer`;

  // Generate capacity slots
  const renderCapacitySlots = () => {
    const slots = [];
    for (let i = 0; i < maxBookable; i++) {
      const isFilled = i < bookingCount;
      const isMySlot = isBooked && i === 0; // First slot is "mine" if booked

      let bgColor = 'bg-gray-200 dark:bg-gray-600';
      let extraClasses = '';

      if (isMySlot && isBooked) {
        bgColor = 'bg-success-500';
        extraClasses = 'ring-2 ring-success-300 dark:ring-success-600';
      } else if (isFilled) {
        bgColor = isFullyBooked ? 'bg-error-500' : 'bg-primary-500';
      }

      slots.push(
        <div key={i} className={`flex-1 h-2 ${bgColor} rounded-full ${extraClasses}`}></div>
      );
    }
    return slots;
  };

  return (
    <>
      <ModalShell
        open={!!event}
        onClose={handleModalClose}
        variant="detail"
        size="md"
        icon={isBooked ? CalendarDaysIcon : CalendarIcon}
        accent={isFullyBooked ? 'danger' : 'brand'}
        title={
          <span className="flex min-w-0 items-center gap-2">
            {events.length > 1 && (
              <button
                type="button"
                onClick={() => setShowEventList(true)}
                className="flex-shrink-0 p-1 -ml-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                aria-label="Zurück zur Terminliste"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
            )}
            <span className="truncate">{getModalTitle()}</span>
            {isBooked && (
              <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-success-50 px-2.5 py-0.5 text-xs font-semibold text-success-700 dark:bg-success-900/20 dark:text-success-400">
                <CheckIcon className="h-3.5 w-3.5" />
                Gebucht
              </span>
            )}
          </span>
        }
        subtitle={
          isBooked ? (
            <span className="text-gray-500 dark:text-gray-400">{getModalSubtitle()}</span>
          ) : (
            <span style={{ color: getAccentColor() }} className="font-medium">
              {getModalSubtitle()}
            </span>
          )
        }
        footer={
          maxBookable > 0 ? (
            isFullyBooked ? (
              <ModalActions
                primary={{
                  label: 'Ausgebucht',
                  disabled: true,
                  icon: <XMarkIcon className="w-5 h-5" />
                }}
              />
            ) : (
              <ModalActions
                primary={{
                  label: isBooked ? 'Termin stornieren' : 'Jetzt buchen',
                  onClick: handleMainBook,
                  disabled: loading,
                  loading,
                  icon: isBooked ? <XCircleIcon className="w-5 h-5" /> : <CheckCircleIcon className="w-5 h-5" />
                }}
                danger={isBooked}
              />
            )
          ) : undefined
        }
      >
        {/* Angemeldet-Banner (nur bei gebucht, 1:1 Design-Entwurf) */}
        {isBooked && (
          <div className="mb-5 flex items-center gap-3 rounded-xl border border-success-200 bg-success-50 px-4 py-3.5 dark:border-success-700 dark:bg-success-900/20">
            <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-success-600 dark:text-success-400" />
            <span className="text-[0.9375rem] font-medium text-success-800 dark:text-success-300">
              Du bist für diesen Termin angemeldet.
            </span>
          </div>
        )}

        {/* Event-Karte (1:1 Design-Entwurf: zentriert, grüne Mono-Zeit) */}
        <div className="rounded-2xl border border-gray-200 bg-gray-50 px-5 py-5 text-center dark:border-gray-700 dark:bg-gray-900/40">
          <div className="flex flex-col items-center gap-1.5">
            <h4 className="text-lg font-extrabold tracking-tight text-gray-900 line-clamp-2 dark:text-white sm:text-xl">
              {event.title}
            </h4>
            <p className="text-[0.9375rem] text-gray-500 dark:text-gray-400 sm:text-base">
              {format(event.start, 'EEEE, dd. MMMM yyyy', { locale: de })}
            </p>
            <p className="font-mono text-lg font-bold text-primary-600 dark:text-primary-400 sm:text-xl">
              {format(event.start, 'HH:mm')} – {format(event.end, 'HH:mm')} Uhr
            </p>
            <p className="inline-flex items-center justify-center gap-1.5 text-sm text-gray-400 dark:text-gray-500">
              <ClockIcon className="h-3.5 w-3.5" />
              {durationText}
            </p>
          </div>
        </div>

        {/* Content Sections */}
        <div className="mt-5 space-y-5">

          {/* Trainer & Co-Trainer – nebeneinander, wenn ein Co-Trainer vorhanden ist */}
          {(event.extendedProps?.creator || event.extendedProps?.cotrainer) && (
            <div
              className={
                event.extendedProps?.creator && event.extendedProps?.cotrainer
                  ? 'grid grid-cols-1 gap-4 sm:grid-cols-2'
                  : ''
              }
            >
              {event.extendedProps?.creator && (
                <ModalSection title="Trainer">
                  <div className="flex items-center gap-3.5 rounded-xl border border-gray-200 px-3.5 py-3 dark:border-gray-700">
                    <ProfilePicture user={event.extendedProps.creator} size="xl" />
                    <div className="min-w-0">
                      <p className="truncate text-base font-medium text-gray-900 dark:text-white">{event.extendedProps.creator.name}</p>
                      <AttributeLabel iconKey="trainer" size="sm" textClassName="text-sm text-gray-500 dark:text-gray-400">Trainer</AttributeLabel>
                    </div>
                  </div>
                </ModalSection>
              )}

              {event.extendedProps?.cotrainer && (
                <ModalSection title="Co-Trainer">
                  <div className="flex items-center gap-3.5 rounded-xl border border-gray-200 px-3.5 py-3 dark:border-gray-700">
                    <ProfilePicture user={event.extendedProps.cotrainer} size="xl" />
                    <div className="min-w-0">
                      <p className="truncate text-base font-medium text-gray-900 dark:text-white">{event.extendedProps.cotrainer.name}</p>
                      <AttributeLabel iconKey="trainer" size="sm" textClassName="text-sm text-gray-500 dark:text-gray-400">Co-Trainer</AttributeLabel>
                    </div>
                  </div>
                </ModalSection>
              )}
            </div>
          )}

          {/* Kapazität (1:1: Icon + belegt + Fortschrittsbalken + Badge) */}
          {maxBookable > 0 && (
            <div className="flex items-center gap-3">
              <UserGroupIcon className="h-4 w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
              <span className="whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                <strong className="font-mono text-gray-900 dark:text-white">{bookingCount}/{maxBookable}</strong> belegt
              </span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <div
                  className={`h-full rounded-full ${isFullyBooked ? 'bg-error-500' : 'bg-primary-600'}`}
                  style={{ width: `${Math.min(100, Math.round((bookingCount / maxBookable) * 100))}%` }}
                />
              </div>
              {isFullyBooked ? (
                <span className="inline-flex flex-shrink-0 items-center rounded-full bg-error-50 px-2 py-0.5 text-xs font-semibold text-error-700 dark:bg-error-900/20 dark:text-error-400">
                  ausgebucht
                </span>
              ) : (
                <span className="inline-flex flex-shrink-0 items-center rounded-full bg-success-50 px-2 py-0.5 text-xs font-semibold text-success-700 dark:bg-success-900/20 dark:text-success-400">
                  {freeSlots} frei
                </span>
              )}
            </div>
          )}

          {/* Warning Section (Issues) */}
          {myIssues && myIssues.length > 0 && (
            <ModalSection title="Hinweis">
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-700">
                <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                  {myIssues.length} Voraussetzung{myIssues.length > 1 ? 'en' : ''} nicht erfüllt
                </p>
                <ul className="mt-2 space-y-1 text-sm text-amber-700 dark:text-amber-400">
                  {myIssues.map((issue, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <XMarkIcon className="w-4 h-4 text-error-500 flex-shrink-0" />
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            </ModalSection>
          )}

          {/* Info Section (if fully booked) */}
          {isFullyBooked && !isBooked && (
            <ModalSection title="Info">
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Dieser Termin ist leider bereits ausgebucht. Schau dir andere Termine an oder wende dich an den Trainer.
                </p>
              </div>
            </ModalSection>
          )}

          {/* Optionen (1:1: 2×2 Outline-Buttons, Löschen destruktiv) */}
          {canManageEvent && (
            <div>
              <p className="mb-2.5 text-[0.6875rem] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Optionen
              </p>
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  onClick={() => setShowParticipantsModal(true)}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-900 transition-colors hover:border-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:hover:border-gray-500"
                >
                  <UserGroupIcon className="h-4 w-4" />
                  Teilnehmer
                </button>

                <button
                  onClick={() => setShowGuestModal(true)}
                  disabled={isFullyBooked}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-900 transition-colors hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:hover:border-gray-500"
                >
                  <UserPlusIcon className="h-4 w-4" />
                  Gast hinzufügen
                </button>

                {onEdit && (
                  <button
                    onClick={() => onEdit(event)}
                    className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-900 transition-colors hover:border-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:hover:border-gray-500"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Bearbeiten
                  </button>
                )}

                {!isPastEvent && (
                  <button
                    onClick={handleDelete}
                    className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-error-600 bg-error-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-error-700"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Löschen
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </ModalShell>

      {/* ConfirmationModal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        destructive={confirmModal.variant === 'two' && confirmModal.type === 'warning' && /stornier/i.test(confirmModal.title || '')}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: confirmModal.onConfirm
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
          },
          confirm: {
            text: confirmModal.type === 'warning' && confirmModal.title.includes('stornieren') ? 'Stornieren' :
                   confirmModal.type === 'warning' ? 'Trotzdem buchen' : 'Bestätigen',
            onPress: confirmModal.onConfirm
          }
        } : undefined}
      />

      {/* Guest Modal */}
      {showGuestModal && (
        <AddGuestModal
          event={event}
          isOpen={showGuestModal}
          onClose={() => setShowGuestModal(false)}
          onBook={() => {
            fetchParticipants();
            checkBookingStatus();
            refreshCalendar();
            setShowGuestModal(false);
          }}
          checkBooking={checkBookingStatus}
          refreshEvent={refreshCalendar}
        />
      )}

      {/* Participants Modal */}
      <ParticipantsModal
        event={event}
        isOpen={showParticipantsModal}
        onClose={() => setShowParticipantsModal(false)}
      />

      {/* Delete Event Modal */}
      <DeleteEventModal
        event={event}
        onDelete={handleDeleteConfirm}
        onClose={() => setShowDeleteModal(false)}
        user={user}
        isPastEvent={isPastEvent}
        isOpen={showDeleteModal}
      />
    </>
  );
};

export default EventModal;
